import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import FieldsComponent from "@/components/form/FieldsComponent.vue";
import Button from "@/components/ButtonComponent.vue";
import LanguageSwitch from "@/components/LanguageSwitch.vue";
import SimpleToggleComponent from "@/components/form/SimpleToggleComponent.vue";
import ButtonNavigation from "@/components/ButtonNavigation.vue";
import NavTabs from "@/components/form/NavTabsComponent.vue";
import CompanyCode from "@/components/form/CompanyCodeComponent.vue";
import ResultView from "@/views/ResultView.vue";
import ResultViewSelfEmployed from "@/views/ResultViewSelfEmployed.vue";
import ResultViewAt from "@/views/ResultViewAt.vue";
import ResultViewFesto from "@/views/ResultViewFesto.vue";
import ChangeCompanyButton from "@/components/ChangeCompanyButton.vue";

// Types and Interfaces
import { getColoringFromUrl } from "@/helper/coloring-helper";
import {FormMode, VALID_COUNTRIES} from "@/enums/Application";
import { AxiosError } from "axios";
import { ApiFormJsonPayload } from "@/interfaces/api-payloads";
import { IFormValues } from "@/store/interfaces/IFormValues";

// Vue tooling
import { ComponentPublicInstance, computed, onBeforeMount, ref, watch } from "vue";
import { store } from "@/store/store";
import { useTranslator } from "@/composables/useTranslator";
import { useGtm } from "@gtm-support/vue-gtm";
import { Api } from "@/services/api-service";
import { defaultEmployer } from "@/store/defaults";
import { mapFormDefaultValues } from "@/mapper/form-defaults-mapper";
import { validateform } from "@/validation/validateform";
import { useUrlParams } from "@/composables/useRoutingParameters";
import { IApiResult } from "@/interfaces/api-result.employee";

// Store and global i18n translator

export default /*#__PURE__*/_defineComponent({
  __name: 'CalculatorElement',
  setup(__props, { expose: __expose }) {
  __expose();

_useCssVars(_ctx => ({
  "9df20ecc-colors\.primary\.hex": (colors.value.primary.hex),
  "9df20ecc-colors\.secondary\.hex": (colors.value.secondary.hex),
  "9df20ecc-colors\.primary\.r": (colors.value.primary.r),
  "9df20ecc-colors\.primary\.g": (colors.value.primary.g),
  "9df20ecc-colors\.primary\.b": (colors.value.primary.b),
  "9df20ecc-colors\.secondary\.r": (colors.value.secondary.r),
  "9df20ecc-colors\.secondary\.g": (colors.value.secondary.g),
  "9df20ecc-colors\.secondary\.b": (colors.value.secondary.b)
}))

// Child components
const { t } = useTranslator();
const $gtm = useGtm();

// Employer data
const employer = computed(() => store.state.employer);
const code = ref<string>("");


/**********  COLOR HANDLING *********+*/
const colors = ref({
  primary: store.getters.getPrimaryColor,
  secondary: store.getters.getSecondaryColor
});
watch(() => store.state.employer.primaryColor, () => {
  colors.value.primary = store.getters.getPrimaryColor;
});

watch(() => store.state.employer.secondaryColor, () => {
  colors.value.secondary = store.getters.getSecondaryColor;
});


/**********  MODAL COMPONENT *********+*/
const modal = ref<number>(1);
const showModal = ref<boolean>(true);
const toggleModal = () => {
  showModal.value = !showModal.value;
};


/*************** Form settings AND URL Parameters ***************/
const formType = ref<FormMode>(store.state.appData.formType);
const formNotFound = ref<boolean>(false);

// If the formtype is not found, hide the modal (because we will show the errorMsg)
watch(formNotFound, () => {

  if (formNotFound.value) {
    showModal.value = false;
  }
});

const { getUrlParam, isUrlParam } = useUrlParams();

const getCodeByURL = async () => {
  const token = getUrlParam("token");
  let urlCode = getUrlParam("code");

  // TODO: What for we need this?
  if (token) {
    // const response = await fetch("https://calculator-backend-dev.bikeleasing.de/api/v1/employer?token="+token),
    // data = await response.json();
    // companyData.value = data;
    // code.value = data.toString();
  } else {
    if (urlCode) {
      urlCode = urlCode.toUpperCase();
      store.commit("setCode", urlCode);
      const attemptToSetEmployerByCode = store.dispatch("setEmployerByCompanyCode", urlCode);

      if (attemptToSetEmployerByCode) {
        showModal.value = false;
      } else {
        store.dispatch("resetEmployer");
        showModal.value = true;
      }
    }
  }
};

const getFormByURL = () => {

  let formMode: FormMode = FormMode.EMPLOYEE;
  const formTypeParam = getUrlParam("form");

  if(formTypeParam && !isUrlParam('form', '')) {
    formMode = formTypeParam as FormMode;
  }  

  if(isUrlParam('country', VALID_COUNTRIES.AUSTRIA) && formMode === FormMode.EMPLOYEE || formMode === FormMode.EMPLOYEE_AT) {
    formMode = FormMode.EMPLOYEE_AT;
  }

  if (formMode) {
    formType.value = formMode;
    store.commit("setFormType", formType.value);
  }
};

/**
 * Check if we need the modal. This is per default the case
 * exception is, if the URL explicitly states that the modal should not be shown ?modal=0
 */
const getModalByURL = () => {
  const modalUrl = getUrlParam("modal");

  if (modalUrl) {
    modal.value = Number(modalUrl);
  }

  if (modal.value === 0) {
    showModal.value = false;
  }

};

/**
 * At moment we don't support Collective Agreement and Beamte for Austria
 *
 */
const checkIfFormShouldExist = () => {

  const countryNotGermany = store.state.appData.country !== VALID_COUNTRIES.GERMANY;
  const formIsOfficial = store.state.appData.formType === FormMode.OFFICIAL;
  const formIsCollectiveAgreement = store.state.appData.formType === FormMode.COLLECTIVE_AGREEMENT;

  formNotFound.value = countryNotGermany && (formIsOfficial || formIsCollectiveAgreement);
};


// Computed properties
const hasExpandedMode = computed<boolean>(() => {
  return store.state.appData.formType != FormMode.SELF_EMPLOYED
    && store.state.appData.formType != FormMode.OFFICIAL
    && store.state.appData.formType != FormMode.SELF_EMPLOYED_AT;
});
const formClass = computed(() => {
  return !showModal.value ? "container-fluid calculator" : "container-fluid calculator blurred";
});

const getLanguageParams = computed(()=> {
  return getUrlParam('hideFlag') && getUrlParam('hideFlag').toLocaleLowerCase() === 'true';
});


const steps = ref<ApiFormJsonPayload>({} as ApiFormJsonPayload);
const activeStep = ref<number>(0);

const changeStepTo = (step: number, submitted = false) => {

  if (submitted) {
    // From submit to last input screen
    activeStep.value = steps.value.length - 2;
    submit.value = false;
  } else if (step === steps.value.length - 1) {
    // Is clicking on the result button, calculate the result
    submitForm();
    submit.value = true;
  } else {
    if (validateform(store.state, t) && typeof step !== "undefined") {
      activeStep.value = step;
      submit.value = false;
    }
  }
};


const errorMsg = ref<string>("");

const initData = async () => {
  await Api.getForm(formType.value).then((response) => {
    const form = response?.data ?? null;

    if (form) {
      store.commit("setForm", formType.value);
      // TODO: This will be possibly collide with setEmployer, since both modifiy the same fields
      mapFormDefaultValues(store.state);
      steps.value = form;
    } else {
      formNotFound.value = true;
      showModal.value = false;
    }
  }).catch(error => console.log(error));
};


/***************** RESULT HANDLING *****************/
const resultData = ref<IApiResult>({} as IApiResult);
const formData = ref<IFormValues>(store.state.formValues);
const submit = ref<boolean>(false);

const resultComponent = computed<ComponentPublicInstance>((): ComponentPublicInstance => {

  // TODO: Make this form generally available and not dependent of single codes
  // TODO: Think about a better typing
  if (code.value === "5G2X" || formType.value === FormMode.COLLECTIVE_AGREEMENT) {
    return ResultViewFesto as unknown as ComponentPublicInstance;
  }

  if ([FormMode.SELF_EMPLOYED, FormMode.SELF_EMPLOYED_AT].includes(formType.value)) {
    return ResultViewSelfEmployed as unknown as ComponentPublicInstance;
  }

  if (formType.value === FormMode.EMPLOYEE_AT) {
    return ResultViewAt as unknown as ComponentPublicInstance;
  }

  return ResultView as unknown as ComponentPublicInstance;

});

const submitForm = async () => {
  let error: unknown | Error | AxiosError;
  try {
    if (employer.value !== defaultEmployer) {
      Api.setEmployer(employer.value);
    }
    Api.setState(store.state);
    resultData.value = await Api.getCalculationResult(formType.value, formData.value);
    submit.value = true;
    activeStep.value = steps.value.length -1 ;
  } catch (e: unknown | Error | AxiosError) {
    if (e instanceof AxiosError) {
      errorMsg.value = e.response.data.message;
    }
    submit.value = false;
    error = e;
  } finally {
    if (!error) {
      if (isUrlParam('track','true') && $gtm.enabled()) {
        const country: string = store.state.appData.country.toLowerCase() ?? "de";
        window.dataLayer?.push({
          "event": "form-sent",
          "form-id": `bikeleasing_${country.toLowerCase()}_leasing_calculator`
        });
      }
    }
  }
};


// Vue Lifecycle Hooks
onBeforeMount(async () => {

  // Try to get the code
  getCodeByURL();

  // Try to get / ovveride the colors
  getColoringFromUrl();

  // Check if we need the modal
  getModalByURL();

  // Try to get the form from the URL
  getFormByURL();

  // Check if the form should exist
  checkIfFormShouldExist();

  // Only run in normal mode, JEST will cause an Network Error 
  // due to the fact that /json/calculator.json is not found
  if(process.env.JEST_WORKER_ID === undefined) {
    await initData();
  }

});


const __returned__ = { t, $gtm, employer, code, colors, modal, showModal, toggleModal, formType, formNotFound, getUrlParam, isUrlParam, getCodeByURL, getFormByURL, getModalByURL, checkIfFormShouldExist, hasExpandedMode, formClass, getLanguageParams, steps, activeStep, changeStepTo, errorMsg, initData, resultData, formData, submit, resultComponent, submitForm, FieldsComponent, Button, LanguageSwitch, SimpleToggleComponent, ButtonNavigation, NavTabs, CompanyCode, ChangeCompanyButton }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})